import React, { useState } from "react";
import {
  BarChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useApi } from "../hooks/useApi";
import { format } from "date-fns";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "16px" };
const tbl  = { width: "100%", borderCollapse: "collapse", fontSize: "11px" };
const th   = { padding: "5px 8px", textAlign: "left", color: "#475569", borderBottom: "1px solid #1e2a45", fontWeight: 500, whiteSpace: "nowrap" };
const td   = { padding: "5px 8px", borderBottom: "1px solid #0f172a", whiteSpace: "nowrap" };

const toggleBtn = (active) => ({
  padding: "4px 10px", borderRadius: "5px", cursor: "pointer", fontSize: "11px",
  background: active ? "#1e40af" : "#1e2a45", color: active ? "#fff" : "#94a3b8",
  border: "none", fontWeight: 600,
});

const kpiBox = { background: "#0a0e1a", borderRadius: "8px", padding: "10px 14px", flex: 1, minWidth: 0 };

function BarProgress({ pct, color }) {
  return (
    <div style={{ height: "6px", borderRadius: "3px", background: "#1e2a45", marginTop: "4px", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, borderRadius: "3px", transition: "width 0.5s" }} />
    </div>
  );
}

function pctColor(v) {
  if (v > 0) return "#4ade80";
  if (v < 0) return "#f87171";
  return "#94a3b8";
}

function formatPeriod(row, view) {
  if (!row.period_start) return "—";
  // period_start is a UTC-midnight ISO string. Build the date from its Y-M-D parts so
  // a viewer west of UTC does not see the previous day.
  const [y, m, d] = row.period_start.slice(0, 10).split("-").map(Number);
  return view === "weekly"
    ? format(new Date(y, m - 1, d), "MMM dd")
    : format(new Date(y, m - 1, 1), "MMM yyyy");
}

const RETURN_MODES = [
  { key: "injected", label: "vs Injected Capital" },
  // Realized balance at the period start (open positions' floating P&L excluded) — see
  // database._build_return_buckets. The API key stays return_pct_vs_equity.
  { key: "equity",   label: "vs Period-Start Balance" },
  { key: "both",     label: "Both" },
];

export default function ReturnsPanel() {
  const { data: weeklyData, loading: wLoading, error: wError }  = useApi("/returns/weekly?weeks=12", 60000);
  const { data: monthlyData, loading: mLoading, error: mError } = useApi("/returns/monthly?months=12", 60000);

  const [view, setView]             = useState("weekly");
  const [returnMode, setReturnMode] = useState("injected");

  // The API sends { buckets: [...] }, oldest first, one bucket per calendar period
  // ending with the CURRENT one (empty periods included) — so the last bucket is this
  // week / this month. v5.22: this read `periods`, a key the API never sent, so the tab
  // was always empty. tests/test_returns_panel.py fails if the two sides drift again.
  const weekly  = weeklyData?.buckets  || [];
  const monthly = monthlyData?.buckets || [];
  const rows    = view === "weekly" ? weekly : monthly;

  const pctKey   = returnMode === "equity" ? "return_pct_vs_equity" : "return_pct_vs_injected";
  const curWeek  = weekly.length  > 0 ? weekly[weekly.length - 1]   : null;
  const curMonth = monthly.length > 0 ? monthly[monthly.length - 1] : null;
  const weekReturnPct = curWeek?.[pctKey] ?? 0;
  const monthPct      = curMonth?.[pctKey] ?? 0;
  const progressToTarget = Math.min(100, (Math.abs(monthPct) / 9) * 100);

  const chartData = rows.map((r) => ({
    label:        formatPeriod(r, view),
    gross_pnl:    r.gross_pnl || 0,
    return_pct_vs_injected: r.return_pct_vs_injected || 0,
    return_pct_vs_equity:   r.return_pct_vs_equity || 0,
  }));

  const loading  = view === "weekly" ? wLoading : mLoading;
  // A backend failure returns { buckets: [], error } — show it rather than letting it
  // read as "no trades".
  const apiError = view === "weekly"
    ? (weeklyData?.error || wError)
    : (monthlyData?.error || mError);

  return (
    <div style={{ padding: "10px" }}>
      {/* KPI Cards */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "12px", flexWrap: "wrap" }}>
        <div style={kpiBox}>
          <div style={{ fontSize: "10px", color: "#64748b" }}>This Week Return</div>
          <div style={{ fontSize: "18px", fontWeight: 700, color: pctColor(weekReturnPct) }}>
            {weekReturnPct >= 0 ? "+" : ""}{weekReturnPct.toFixed(2)}%
          </div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: "10px", color: "#64748b" }}>This Month Return</div>
          <div style={{ fontSize: "18px", fontWeight: 700, color: pctColor(monthPct) }}>
            {monthPct >= 0 ? "+" : ""}{monthPct.toFixed(2)}%
          </div>
        </div>
        <div style={kpiBox}>
          <div style={{ fontSize: "10px", color: "#64748b" }}>Monthly Target (9%)</div>
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#60a5fa" }}>
            {progressToTarget.toFixed(0)}% of target
          </div>
          <BarProgress pct={progressToTarget} color={monthPct >= 0 ? "#60a5fa" : "#f87171"} />
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
          <span style={{ fontWeight: 600, color: "#94a3b8", fontSize: "13px" }}>💹 Returns</span>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {/* Weekly / Monthly toggle */}
            {["weekly", "monthly"].map((v) => (
              <button key={v} style={toggleBtn(view === v)} onClick={() => setView(v)}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
            <div style={{ width: "1px", background: "#1e2a45", margin: "0 4px" }} />
            {/* Return mode toggle */}
            {RETURN_MODES.map((m) => (
              <button key={m.key} style={toggleBtn(returnMode === m.key)} onClick={() => setReturnMode(m.key)}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div style={{ color: "#475569", fontSize: "12px", padding: "20px 0", textAlign: "center" }}>Loading returns data...</div>
        )}

        {!loading && apiError && (
          <div style={{ color: "#f87171", textAlign: "center", padding: "12px 0", fontSize: "12px" }}>
            Returns unavailable: {String(apiError)}
          </div>
        )}

        {!loading && !apiError && chartData.length === 0 && (
          <div style={{ color: "#334155", textAlign: "center", padding: "30px 0", fontSize: "12px" }}>
            No return data yet
          </div>
        )}

        {!loading && chartData.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2a45" />
                <XAxis dataKey="label" tick={{ fill: "#475569", fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis yAxisId="pnl" tick={{ fill: "#475569", fontSize: 9 }} tickFormatter={(v) => `$${v.toFixed(0)}`} width={50} />
                <YAxis yAxisId="pct" orientation="right" tick={{ fill: "#475569", fontSize: 9 }} tickFormatter={(v) => `${v.toFixed(1)}%`} width={45} />
                <Tooltip
                  contentStyle={{ background: "#1e2a45", border: "1px solid #334155", borderRadius: "6px", fontSize: "11px" }}
                  formatter={(v, name, item) => {
                    // Match on dataKey: the Bar's display name is "Gross P&L", which
                    // never contained "pnl", so money used to render as a percent.
                    if (item?.dataKey === "gross_pnl") return [`$${v.toFixed(2)}`, name];
                    return [`${v.toFixed(2)}%`, name];
                  }}
                />
                <Bar yAxisId="pnl" dataKey="gross_pnl" name="Gross P&L"
                     fill="#1e40af" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                {returnMode !== "equity" && (
                  <Line yAxisId="pct" type="monotone" dataKey="return_pct_vs_injected"
                        stroke="#4ade80" dot={{ r: 3, fill: "#4ade80" }} strokeWidth={2} isAnimationActive={false} />
                )}
                {returnMode !== "injected" && (
                  <Line yAxisId="pct" type="monotone" dataKey="return_pct_vs_equity"
                        stroke="#f59e0b" dot={{ r: 3, fill: "#f59e0b" }} strokeWidth={2} strokeDasharray="5 3" isAnimationActive={false} />
                )}
              </BarChart>
            </ResponsiveContainer>

            {/* Period Table */}
            <div style={{ overflowX: "auto", marginTop: "14px" }}>
              <table style={tbl}>
                <thead>
                  <tr>
                    {["Period", "Trades", "Wins", "Win Rate%", "Gross P&L ($)", "Return % vs Injected", "Return % vs Equity", "Avg R:R"].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const pnlC = (r.gross_pnl || 0) > 0 ? "#4ade80" : (r.gross_pnl || 0) < 0 ? "#f87171" : "#94a3b8";
                    const injC = pctColor(r.return_pct_vs_injected || 0);
                    const eqC  = pctColor(r.return_pct_vs_equity || 0);
                    return (
                      <tr key={i}>
                        <td style={td}><b style={{ color: "#e2e8f0" }}>{formatPeriod(r, view)}</b></td>
                        <td style={td}>{r.trades ?? "—"}</td>
                        <td style={td}>{r.wins ?? "—"}</td>
                        <td style={td}>{r.trades > 0 ? r.win_rate.toFixed(1) : "—"}</td>
                        <td style={{ ...td, color: pnlC, fontWeight: 600 }}>
                          {r.gross_pnl != null ? `${r.gross_pnl >= 0 ? "+" : ""}$${r.gross_pnl.toFixed(2)}` : "—"}
                        </td>
                        <td style={{ ...td, color: injC, fontWeight: 600 }}>
                          {r.return_pct_vs_injected != null ? `${r.return_pct_vs_injected >= 0 ? "+" : ""}${r.return_pct_vs_injected.toFixed(2)}%` : "—"}
                        </td>
                        <td style={{ ...td, color: eqC, fontWeight: 600 }}>
                          {r.return_pct_vs_equity != null ? `${r.return_pct_vs_equity >= 0 ? "+" : ""}${r.return_pct_vs_equity.toFixed(2)}%` : "—"}
                        </td>
                        <td style={td}>{r.avg_rr != null ? r.avg_rr.toFixed(2) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
